import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/services/api";
import SEO from "@/components/SEO";

const BG_IMAGE = "https://images.unsplash.com/photo-1651342489644-be11c7ee318d";

export default function PasswordResetRequestPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const nav = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      return toast.error("Email is required");
    }
    setLoading(true);
    try {
      await api.post("/password-reset/request", { email });
      setEmailSent(true);
      toast.success("If an account exists with this email, a password reset link has been sent.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to request password reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Forgot Password"
        description="Request a password reset link for your Bill Forge account."
        path="/forgot-password"
        noindex={true}
      />
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white dark:bg-[#0A0A0B] text-slate-900 dark:text-slate-100">
        <Toaster position="top-right" />
      {/* Side visual */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0B]/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="text-2xl font-extrabold tracking-tighter">BILL FORGE</div>
          <div>
            <div className="uppercase text-xs tracking-[0.3em] opacity-70 mb-4">Invoicing / Precision</div>
            <div className="text-4xl font-light leading-tight max-w-sm">
              Ship invoices your customers actually respect.
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <button
            onClick={() => nav("/login")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>

          <div className="uppercase text-xs tracking-[0.3em] text-slate-500 mb-3">Recovery</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter mb-2">
            Reset Password.
          </h1>
          <p className="text-slate-500 mb-10">
            {emailSent 
              ? "Check your email for the reset link."
              : "Enter your email to receive a password reset link."
            }
          </p>

          {!emailSent ? (
            <form onSubmit={handleRequestReset} className="space-y-5">
              <label className="block">
                <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Email</span>
                <div className="mt-2 flex items-center gap-3 border border-slate-300 dark:border-slate-700 rounded-sm px-4 py-3 focus-within:border-slate-900 dark:focus-within:border-white transition-colors">
                  <Mail className="w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-transparent outline-none text-lg"
                    required
                  />
                </div>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-4 rounded-sm font-semibold hover:bg-[#002FA7] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? "Sending..." : "Send Reset Link"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-sm p-4">
                <p className="text-green-800 dark:text-green-200">
                  Email sent! Check your inbox for the password reset link.
                </p>
              </div>
              <button
                onClick={() => nav("/login")}
                className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-4 rounded-sm font-semibold hover:bg-[#002FA7] hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                Back to Login <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
    </>
  );
}